/**
 * Comms / Call Log tool at /comms/call-log.
 *
 * The call log for a date range in the shared DataGridShell: incoming,
 * outgoing, and user-to-user extension calls, with who they were trying to
 * reach (by name), the caller (by name, with the number/extension on hover),
 * outcome, wait time, and where an abandoned caller gave up. Date-range +
 * direction + outcome + text filters and a server-side Excel export, following
 * the Orders grid pattern. Repeat incoming callers show a "x N" badge.
 *
 * Top-level Comms tool (no back link, per cockpit-ui-navigation.md). Gated by
 * comms:read (enforced server-side).
 */

import { useMemo, useState } from 'react';
import { Download, Loader2, Phone, Play } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import {
  downloadCommsCallsXlsx,
  fetchCommsRecordingUrl,
  formatDuration,
  formatPhone,
  outcomeLabel,
  outcomeTone,
  todayDate,
  useCommsCalls,
  type CommsCall,
  type CommsCallsFilters,
} from '@/lib/useComms';

type SortCol = 'time' | 'caller' | 'reached' | 'direction' | 'outcome' | 'talk_seconds' | 'wait_seconds';
const NUMERIC_COLS: ReadonlySet<SortCol> = new Set(['talk_seconds', 'wait_seconds']);

const OUTCOME_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All outcomes' },
  { value: 'live', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_message', label: 'No message' },
  { value: 'after_hours', label: 'After hours' },
  { value: 'abandoned', label: 'Abandoned' },
];

const DIRECTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'in', label: 'Incoming' },
  { value: 'out', label: 'Outgoing' },
  { value: 'internal', label: 'Internal' },
  { value: 'all', label: 'All calls' },
];

const DIRECTION_LABEL: Record<string, string> = {
  in: 'Incoming',
  out: 'Outgoing',
  internal: 'Internal',
};

export function CommsCallLog() {
  const [dateFrom, setDateFrom] = useState<string>(todayDate);
  const [dateTo, setDateTo] = useState<string>(todayDate);
  const [outcome, setOutcome] = useState<string>('');
  const [direction, setDirection] = useState<string>('in');
  const [q, setQ] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir } | null>(null);
  const [audio, setAudio] = useState<{ id: string; url: string } | null>(null);
  const [loadingAudio, setLoadingAudio] = useState<string | null>(null);

  const filters: CommsCallsFilters = { date_from: dateFrom, date_to: dateTo, outcome, direction, q };
  const { calls, isLoading, error } = useCommsCalls(filters);

  const onPlay = async (linkedid: string) => {
    setLoadingAudio(linkedid);
    try {
      const url = await fetchCommsRecordingUrl(linkedid);
      setAudio({ id: linkedid, url });
    } catch {
      setAudio(null);
    } finally {
      setLoadingAudio(null);
    }
  };

  // How many times each incoming caller appears in the loaded range, so repeat
  // callers stand out. Keyed on the caller number; only incoming calls count.
  const callerCounts = useMemo<Map<string, number>>(() => {
    const m = new Map<string, number>();
    for (const c of calls) {
      if (c.direction === 'in' && c.caller) m.set(c.caller, (m.get(c.caller) ?? 0) + 1);
    }
    return m;
  }, [calls]);

  // Client-side sort of the loaded range (a bounded list, not a cursor page).
  // No sort = the API's default (most recent first).
  const sortedCalls = useMemo<CommsCall[]>(() => {
    if (!sort) return calls;
    const { col, dir } = sort;
    const s = dir === 'asc' ? 1 : -1;
    const numeric = NUMERIC_COLS.has(col);
    return [...calls].sort((a, b) => {
      const av = a[col] ?? (numeric ? 0 : '');
      const bv = b[col] ?? (numeric ? 0 : '');
      return (numeric ? (av as number) - (bv as number) : String(av).localeCompare(String(bv))) * s;
    });
  }, [calls, sort]);

  const toggleSort = (col: SortCol) =>
    setSort((prev) =>
      !prev || prev.col !== col
        ? { col, dir: 'asc' }
        : prev.dir === 'asc'
          ? { col, dir: 'desc' }
          : null,
    );
  const sortedFor = (col: SortCol): SortDir | null => (sort && sort.col === col ? sort.dir : null);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadCommsCallsXlsx(filters);
    } finally {
      setExporting(false);
    }
  };

  const colCount = 9;

  const body = isLoading ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading calls...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-red-700 dark:text-red-300">
        Could not load calls: {error.message}
      </td>
    </tr>
  ) : calls.length === 0 ? (
    <tr>
      <td colSpan={colCount} className="px-4 py-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        No calls match these filters.
      </td>
    </tr>
  ) : (
    sortedCalls.map((c, i) => {
      const repeat = c.direction === 'in' && c.caller ? callerCounts.get(c.caller) ?? 0 : 0;
      return (
        <tr key={i} className="border-b border-amp-line last:border-0 dark:border-amp-dark-line">
          <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {c.date} {c.time}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {DIRECTION_LABEL[c.direction] ?? c.direction}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink">
            {c.caller_name ? (
              <div className="flex flex-col" title={c.caller_ext ?? undefined}>
                <span className="flex items-center gap-1.5">
                  {c.caller_name}
                  {repeat > 1 && (
                    <span className="rounded bg-amp-cream-warm px-1 text-[10px] font-medium text-amp-olive dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark">
                      x{repeat}
                    </span>
                  )}
                </span>
                <span className="font-mono text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {c.caller ? formatPhone(c.caller) : ''}
                </span>
              </div>
            ) : (
              <span className="flex items-center gap-1.5 font-mono" title={c.caller_ext ?? undefined}>
                {c.caller ? formatPhone(c.caller) : '-'}
                {repeat > 1 && (
                  <span className="rounded bg-amp-cream-warm px-1 text-[10px] font-medium text-amp-olive dark:bg-amp-dark-surface-warm dark:text-amp-olive-dark">
                    x{repeat}
                  </span>
                )}
              </span>
            )}
          </td>
          <td className="px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink" title={c.reached_ext ?? undefined}>
            {c.reached ?? '-'}
          </td>
          <td className="px-3 py-2 text-xs">
            <Pill tone={outcomeTone(c.outcome)}>{outcomeLabel(c.outcome)}</Pill>
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {formatDuration(c.talk_seconds) || '-'}
          </td>
          <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {formatDuration(c.wait_seconds) || '-'}
          </td>
          <td className="px-3 py-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {c.outcome === 'abandoned' ? (c.reached ?? '-') : '-'}
          </td>
          <td className="px-3 py-2 text-xs">
            {c.has_recording ? (
              <button
                type="button"
                onClick={() => onPlay(c.linkedid)}
                disabled={loadingAudio === c.linkedid}
                className="inline-flex items-center gap-1 rounded border border-amp-line px-2 py-1 text-amp-olive hover:bg-amp-cream-warm disabled:opacity-50 dark:border-amp-dark-line dark:text-amp-olive-dark"
                aria-label="Play voicemail"
              >
                {loadingAudio === c.linkedid ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
                Play
              </button>
            ) : (
              '-'
            )}
          </td>
        </tr>
      );
    })
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Comms
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Call Log</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Incoming, outgoing, and internal calls over a date range: who they were trying to reach,
            outcome, and how long they waited.
          </p>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || calls.length === 0}
          className="inline-flex items-center gap-1.5 rounded border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm disabled:opacity-50 dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted" htmlFor="call-log-from">
            From
          </label>
          <input
            id="call-log-from"
            type="date"
            value={dateFrom}
            max={dateTo || todayDate()}
            onChange={(e) => e.target.value && setDateFrom(e.target.value)}
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted" htmlFor="call-log-to">
            To
          </label>
          <input
            id="call-log-to"
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            max={todayDate()}
            onChange={(e) => e.target.value && setDateTo(e.target.value)}
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted" htmlFor="call-log-direction">
            Direction
          </label>
          <select
            id="call-log-direction"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {DIRECTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted" htmlFor="call-log-outcome">
            Outcome
          </label>
          <select
            id="call-log-outcome"
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted" htmlFor="call-log-q">
            Search caller or destination
          </label>
          <input
            id="call-log-q"
            type="text"
            value={q}
            placeholder="phone number or name"
            onChange={(e) => setQ(e.target.value)}
            className="min-w-[12rem] rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
      </div>

      {audio && (
        <div className="flex items-center gap-3 rounded-lg border border-amp-line bg-amp-cream-warm px-4 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Voicemail</span>
          <audio controls autoPlay src={audio.url} className="h-8 flex-1" />
          <button
            type="button"
            onClick={() => setAudio(null)}
            className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            Close
          </button>
        </div>
      )}

      <DataGridShell
        title={
          <span className="inline-flex items-center gap-2">
            <Phone className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
            Call Log
          </span>
        }
        loaded={isLoading ? undefined : calls.length}
        countNoun="calls"
      >
        <table className="min-w-full">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader label="when" sorted={sortedFor('time')} onToggle={() => toggleSort('time')} thClassName="px-3 py-2" />
              <SortableHeader label="direction" sorted={sortedFor('direction')} onToggle={() => toggleSort('direction')} thClassName="px-3 py-2" />
              <SortableHeader label="caller" sorted={sortedFor('caller')} onToggle={() => toggleSort('caller')} thClassName="px-3 py-2" />
              <SortableHeader label="trying to reach" sorted={sortedFor('reached')} onToggle={() => toggleSort('reached')} thClassName="px-3 py-2" />
              <SortableHeader label="status" sorted={sortedFor('outcome')} onToggle={() => toggleSort('outcome')} thClassName="px-3 py-2" />
              <SortableHeader label="talk" sorted={sortedFor('talk_seconds')} onToggle={() => toggleSort('talk_seconds')} align="right" thClassName="px-3 py-2" />
              <SortableHeader label="waited" sorted={sortedFor('wait_seconds')} onToggle={() => toggleSort('wait_seconds')} align="right" thClassName="px-3 py-2" />
              <th className="px-3 py-2 text-left text-xs font-medium">gave up at</th>
              <th className="px-3 py-2 text-left text-xs font-medium">listen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">{body}</tbody>
        </table>
      </DataGridShell>
    </div>
  );
}
